import { useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DesignerChange } from "../designer.board.api";
import { LayoutProductSlots } from "../../../features/layouts/layout.product-slots";
import { slotColor } from "../../../features/layouts/layout.slot-colors";
import { LayoutItemRef, LayoutRef } from "../../../features/layouts/layouts";
import { SlotBinding } from "./layout.preview";

const NEW_PRODUCT = "new";

const slug = (name?: string) =>
  (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 20);

type Props = {
  item: LayoutItemRef;
  layout: LayoutRef;
  slots: LayoutProductSlots;
  binding: SlotBinding;
  onChange: (c: DesignerChange) => void;
};

/**
 * Replaces the free-text "Product Key" retype with a "Same product as…" picker:
 * sharing a product means picking it, not remembering an exact string. The raw
 * key stays available under Advanced (with autocomplete of existing keys) as a
 * power-user escape hatch. Picking a slot that has no key yet assigns a canonical
 * key to that slot's anchor element too, so both sides genuinely share one product.
 */
export const ProductSlotPicker = ({
  item,
  layout,
  slots,
  binding,
  onChange,
}: Props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const items = layout.items ?? [];
  const currentSlot = slots.slotById.get(item.id);
  const siblingCount = currentSlot
    ? items.filter((i) => slots.slotById.get(i.id) === currentSlot).length
    : 0;
  const isShared = !!currentSlot && siblingCount > 1;
  const value = isShared ? `slot:${currentSlot}` : NEW_PRODUCT;

  const slotNumbers = Array.from({ length: slots.total }, (_, i) => i + 1);
  const existingKeys = Array.from(binding.keyToSlot.keys());

  const dispatchKey = (title: string) =>
    onChange({
      type: "update",
      item,
      data: { title },
      action: "share product",
    });

  const canonicalKeyForSlot = (slot: number): string => {
    // reuse an existing key already on that slot, if any
    const keyed = items.find(
      (i) => slots.slotById.get(i.id) === slot && !!slots.keyById.get(i.id)
    );
    if (keyed) return slots.keyById.get(keyed.id)!;

    // otherwise mint a stable, unique key and stamp it on the slot's anchor
    const taken = new Set<string>([
      ...existingKeys,
      ...items.map((i) => (i.di.title ?? "").trim()).filter(Boolean),
    ]);
    const base = slug(binding.productBySlot.get(slot)?.product.name) || `product-${slot}`;
    let key = base;
    let n = 2;
    while (taken.has(key)) key = `${base}-${n++}`;

    const anchor = items.find((i) => slots.slotById.get(i.id) === slot);
    if (anchor) anchor.di.title = key; // stamp the anchor so both sides share
    return key;
  };

  const handleSelect = (v: string) => {
    if (v === NEW_PRODUCT) {
      if (isShared || (item.di.title ?? "").trim()) dispatchKey("");
      return;
    }
    const slot = Number(v.replace("slot:", ""));
    if (!slot || (currentSlot === slot && isShared)) return;
    dispatchKey(canonicalKeyForSlot(slot));
  };

  return (
    <Box sx={{ minWidth: 190 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={`slot-picker-${item.id}`}>Same product as</InputLabel>
        <Select
          labelId={`slot-picker-${item.id}`}
          label="Same product as"
          value={value}
          onChange={(e) => handleSelect(e.target.value)}
          renderValue={(v) =>
            v === NEW_PRODUCT ? (
              "New (separate product)"
            ) : (
              <SlotLabel
                slot={Number((v as string).replace("slot:", ""))}
                binding={binding}
              />
            )
          }
        >
          {slotNumbers.map((s) => (
            <MenuItem key={s} value={`slot:${s}`}>
              <SlotLabel slot={s} binding={binding} />
            </MenuItem>
          ))}
          <MenuItem value={NEW_PRODUCT}>New (separate product)</MenuItem>
        </Select>
      </FormControl>

      <Link
        component="button"
        type="button"
        variant="caption"
        underline="hover"
        onClick={() => setShowAdvanced((s) => !s)}
        sx={{ mt: 0.5, display: "inline-block", color: "text.secondary" }}
      >
        {showAdvanced ? "Hide" : "Advanced"} product key
      </Link>

      {showAdvanced && (
        <Autocomplete
          freeSolo
          size="small"
          options={existingKeys}
          value={item.di.title ?? ""}
          onInputChange={(_, v) => {
            if (v !== (item.di.title ?? "")) dispatchKey(v);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product Key"
              helperText="Elements sharing a key show the same product."
            />
          )}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
};

const SlotLabel = ({ slot, binding }: { slot: number; binding: SlotBinding }) => {
  const name = binding.productBySlot.get(slot)?.product.name;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: slotColor(slot),
          boxShadow: "0 0 0 1px rgba(0,0,0,0.3)",
        }}
      />
      <Typography
        variant="body2"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        Product {slot}
        {name ? ` — ${name}` : ""}
      </Typography>
    </Box>
  );
};
